<template>
  <section class="sessionView">
    <!-- Session progress lives in the queue rail's lead slot (where the
         power-dial control sits when idle) and End session in its foot. -->
    <div class="sessionBody">
      <div v-if="phase === 'preparing'" class="prep">
        <span class="prepDot"></span>
        <span>Connecting your line…</span>
      </div>
      <LiveCallCard
        v-else-if="phase === 'dialing' || phase === 'live'"
        :entry="current"
        :from-e164="fromE164"
        :duration-seconds="durationSeconds"
        :ringing="isRinging"
        :connected="phase === 'live'"
        :active-e164="claim?.prospect_e164"
        :session-lines="sessionLines"
        :machine-detected="machineDetected"
        :checking-line="checkingLine"
        :notes="notes"
        :group-color="groupColor"
        :media-fault="mediaFault"
        :media-notice="mediaNotice"
        :sent-sequences="sentSequences"
        @update:notes="notes = $event"
        @skip="skip"
        @quick-dispose="quickDispose"
        @hangup="hangUp"
        @send-digits="sendDigits"
        @redial="redial"
      />
      <DispositionPanel
        v-else-if="phase === 'disposing'"
        :entry="current"
        :answered="answered"
        :disposition="pendingDisposition"
        :notes="notes"
        :duration-seconds="durationSeconds"
        :error="dispositionError"
        :submitting="submitting"
        @update:disposition="pendingDisposition = $event"
        @update:notes="notes = $event"
        @save="saveAndNext"
      />
      <SessionEndScreen
        v-else-if="isEnd"
        :variant="endVariant"
        :stats="stats"
        :error-detail="errorDetail"
        @back="onBack"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import LiveCallCard from './LiveCallCard.vue'
import DispositionPanel from './DispositionPanel.vue'
import SessionEndScreen from './SessionEndScreen.vue'
import { useDialerSession } from '../../composables/useDialerSession'

const props = defineProps<{ playColor: (id: string | null | undefined) => string }>()

const session = useDialerSession()
const {
  phase,
  current,
  stats,
  errorDetail,
  dispositionError,
  answered,
  pendingDisposition,
  notes,
  submitting,
  durationSeconds,
  isRinging,
  claim,
  sessionLines,
  mediaFault,
  mediaNotice,
  sentSequences,
  machineDetected,
  checkingLine,
  skip,
  redial,
  quickDispose,
  hangUp,
  sendDigits,
  saveAndNext,
  reset,
} = session

const fromE164 = computed(() => claim.value?.from_phone_e164 ?? null)
const groupColor = computed(() => props.playColor(current.value?.sales_play_id))
const isEnd = computed(() => ['error_before', 'error_during', 'complete'].includes(phase.value))
type EndVariant = 'error_before' | 'error_during' | 'nice'
const endVariant = computed<EndVariant>(() => {
  if (phase.value === 'complete') return 'nice'
  return phase.value as 'error_before' | 'error_during'
})

function onBack(): void {
  // reset() → phase 'idle'; HumanDialerQueuePage watches that transition and
  // refetches the queue (covers session end-screens AND one-off completion).
  reset()
}
</script>

<style scoped>
.sessionView { flex: 1; min-width: 0; min-height: 0; display: flex; flex-direction: column; position: relative; overflow: hidden; background: var(--color-bg-app); }
.sessionBody { flex: 1; min-height: 0; display: flex; flex-direction: column; overflow: hidden; }
.prep { flex: 1; display: flex; align-items: center; justify-content: center; gap: var(--space-3); color: var(--color-text-tertiary); font-size: var(--fs-sm); }
.prepDot { width: 10px; height: 10px; border-radius: 999px; background: var(--color-accent); animation: prepPulse 1s var(--ease-out) infinite; }
@keyframes prepPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
</style>
